import type { Order, TrackingEvent } from './types';
import { DELIVERED_STATUS, PARXL_DELIVERED_STATUS, OrderStatus } from '../../../constants/order';
import { formatLongDate, formatDateTime } from '../../../utils/date';

/**
 * Checks if the order is a PARXL order
 */
const isParxlOrder = (order: Order): boolean => {
  const provider = order.shippingData?.provider;
  return provider === 'PARXL' || (!provider && !!order.parxlData);
};

/**
 * Transforms Deleo trace events to UI TrackingEvent format
 * API returns timestamps in KST (Korea Standard Time, UTC+9)
 */
const transformDeleoTrackingEvents = (order: Order): TrackingEvent[] => {
  const traceList = order.deleoData?.deliveryTrace?.traceInfoList?.[0]?.traceList;

  if (!traceList || traceList.length === 0) {
    return [];
  }

  return traceList.map((event, index) => {
    const isFirstEvent = index === 0;
    const isDelivered = event.status === DELIVERED_STATUS;
    const isCurrentEvent = isFirstEvent && !isDelivered;

    const eventStatus: TrackingEvent['status'] = isCurrentEvent ? 'current' : 'completed';

    // API format: "2025-12-18 09:08:00" in KST
    // Add KST timezone offset (+09:00) to ensure correct interpretation
    const eventDate = new Date(event.eventTime.replace(' ', 'T') + '+09:00');
    const formattedTimestamp = formatDateTime(eventDate);

    return {
      id: `${index}-${event.eventTime}`,
      type: event.status,
      title: event.description,
      timestamp: formattedTimestamp,
      status: eventStatus,
    };
  });
};

/**
 * Transforms PARXL tracking events to UI TrackingEvent format
 * API returns timestamps in ISO 8601 format (UTC)
 */
const transformParxlTrackingEvents = (order: Order): TrackingEvent[] => {
  const trackingStatus = order.parxlData?.trackingStatus;

  if (!trackingStatus || trackingStatus.length === 0) {
    return [];
  }

  return trackingStatus.map((event, index) => {
    const isFirstEvent = index === 0;
    const isDelivered = event.status === PARXL_DELIVERED_STATUS;
    const isCurrentEvent = isFirstEvent && !isDelivered;

    const eventStatus: TrackingEvent['status'] = isCurrentEvent ? 'current' : 'completed';

    // API format: ISO 8601 "2025-12-29T22:18:01.706Z"
    const eventDate = new Date(event.date);
    const formattedTimestamp = formatDateTime(eventDate);

    return {
      id: `${index}-${event.date}`,
      type: event.status,
      title: event.status,
      timestamp: formattedTimestamp,
      description: event.location,
      status: eventStatus,
    };
  });
};

/**
 * Transforms tracking events to UI TrackingEvent format
 * Supports both Deleo and PARXL providers
 */
export const transformTrackingEvents = (order: Order): TrackingEvent[] => {
  if (isParxlOrder(order)) {
    return transformParxlTrackingEvents(order);
  }

  return transformDeleoTrackingEvents(order);
};

/**
 * Gets the tracking number from the order
 * Returns the tracking number that matches the carrier:
 * - Deleo with local carrier (FedEx, DHL, etc.): Returns local carrier tracking number
 * - PARXL: Returns PARXL tracking number
 * - Fallback: Returns Deleo tracking number
 */
export const getTrackingNumber = (order: Order): string => {
  // PARXL - use parxlData.plsShipmentReference or shippingData.trackingNumber
  if (isParxlOrder(order)) {
    return order.parxlData?.plsShipmentReference || order.shippingData?.trackingNumber || '';
  }

  // Deleo with local carrier - use local carrier tracking number
  const localTrackingNumber = order.deleoData?.orderTrackingList?.[0]?.trackingNo;
  if (localTrackingNumber) {
    return localTrackingNumber;
  }

  // Fallback to Deleo tracking number
  if (order.deleoData?.deleoTrackingNo) {
    return order.deleoData.deleoTrackingNo;
  }

  // Final fallback to shippingData.trackingNumber
  if (order.shippingData?.trackingNumber) {
    return order.shippingData.trackingNumber;
  }

  return '';
};

/**
 * Gets the delivery date from Deleo tracking events
 * Returns the date of the most recent tracking event
 */
const getDeleoDeliveryDate = (order: Order): string | undefined => {
  const traceList = order.deleoData?.deliveryTrace?.traceInfoList?.[0]?.traceList;
  const latestEvent = traceList?.[0];

  if (latestEvent?.eventTime) {
    // API format: "2025-12-18 09:08:00" in KST
    const date = new Date(latestEvent.eventTime.replace(' ', 'T') + '+09:00');
    return formatLongDate(date);
  }

  return undefined;
};

/**
 * Gets the delivery date from PARXL tracking events
 * Returns the date of the most recent tracking event
 */
const getParxlDeliveryDate = (order: Order): string | undefined => {
  const trackingStatus = order.parxlData?.trackingStatus;
  const latestEvent = trackingStatus?.[0];

  if (latestEvent?.date) {
    // API format: ISO 8601 "2025-12-29T22:18:01.706Z"
    const date = new Date(latestEvent.date);
    return formatLongDate(date);
  }

  return undefined;
};

/**
 * Gets the delivery date from the order tracking events
 * Only shows delivery date when order status is 'completed'
 */
export const getDeliveryDate = (order: Order): string | undefined => {
  // Only show delivery date when order is completed
  if (order.status !== OrderStatus.Completed) {
    return undefined;
  }

  if (isParxlOrder(order)) {
    return getParxlDeliveryDate(order);
  }

  return getDeleoDeliveryDate(order);
};

/**
 * Gets the carrier name from the order
 * - Deleo: Returns local carrier name from orderTrackingList (e.g., "FedEx", "DHL"), fallback to "DELEO"
 * - PARXL: Returns "PARXL"
 * - Returns null if no carrier data available
 */
export const getCarrier = (order: Order): string | null => {
  if (isParxlOrder(order)) {
    return 'PARXL';
  }

  // Deleo - get local carrier from orderTrackingList, fallback to "DELEO"
  if (order.deleoData) {
    const carrierName = order.deleoData.orderTrackingList?.[0]?.deliveryCompanyCd?.name;
    return carrierName ?? 'DELEO';
  }

  return null;
};

/**
 * Gets the order date formatted
 * orderDateTime is in GMT without timezone suffix, so we add 'Z' to indicate UTC
 */
export const getFormattedOrderDate = (order: Order): string => {
  // API format: "2025-12-11T13:08:19" in GMT (UTC+0)
  // Add 'Z' suffix to ensure correct interpretation as UTC
  const date = new Date(`${order.orderDateTime}Z`);
  return formatLongDate(date);
};

/**
 * Generates the receipt URL for an order
 */
export const getReceiptUrl = (order: Order): string => {
  return `/my-account/view-order/${order.orderId}/`;
};

/**
 * Calculates the item price from line item data
 * Uses direct price if available, otherwise calculates from total and quantity
 */
const calculateItemPrice = (item: Order['lineItems'][0]): number => {
  if (item.price !== undefined) {
    return item.price;
  }

  if (item.total !== undefined && item.quantity > 0) {
    return item.total / item.quantity;
  }

  return 0;
};

interface TransformOrderOptions {
  isRegistered?: boolean;
}

/**
 * Transforms Order API response to display format for OrderCard
 * @param order - The order data from API
 * @param options - Transform options
 * @param options.isRegistered - If true, includes receiptUrl (for logged-in users only)
 */
export const transformOrderForDisplay = (order: Order, options: TransformOrderOptions = {}) => {
  const { isRegistered = false } = options;

  return {
    id: Number(order.orderId),
    orderNumber: `#${order.orderId}`,
    status: order.status,
    orderDate: getFormattedOrderDate(order),
    deliveryDate: getDeliveryDate(order),
    trackingNumber: getTrackingNumber(order),
    carrier: getCarrier(order),
    total: order.totalAmount,
    currency: order.currency,
    items: order.lineItems.map((item, index) => ({
      id: index,
      productId: item.productId ?? item.parentProductId,
      name: item.name,
      imageUrl: item.wooCommerceImageUrl,
      variant: [item.productSize, item.productPack].filter(Boolean).join(' '),
      quantity: item.quantity,
      price: calculateItemPrice(item),
      brand: item.brand,
      category: item.category,
    })),
    trackingEvents: transformTrackingEvents(order),
    receiptUrl: isRegistered ? getReceiptUrl(order) : undefined,
  };
};
